/* SPRINT 8 (recette finale PO) — traduction CANONIQUE des erreurs de
 * validation FastAPI/Pydantic en langage maison (FR par défaut, EN pour les
 * pages publiques anglaises).
 *
 * Règle absolue : plus JAMAIS de message Pydantic anglais brut rendu à
 * l'utilisateur. Tout item non reconnu retombe sur un générique français.
 *
 * Module PUR (zéro dépendance, zéro React) : il est exécuté tel quel par les
 * verrous pytest via node (tests/test_sprint8_public_v3.py —
 * TestValidationErrorsFrench). Ne rien importer ici.
 */

const FIELD_LABELS = {
  fr: {
    first_name: 'Prénom',
    last_name: 'Nom',
    email: 'Adresse e-mail',
    subject: 'Objet',
    message: 'Message',
    phone: 'Téléphone',
    country: 'Pays',
    company: 'Société',
    password: 'Mot de passe',
    investor_type: "Profil d'investisseur",
    estimated_investment_range: "Montant envisagé",
  },
  en: {
    first_name: 'First name',
    last_name: 'Last name',
    email: 'E-mail address',
    subject: 'Subject',
    message: 'Message',
    phone: 'Phone',
    country: 'Country',
    company: 'Company',
    password: 'Password',
    investor_type: 'Investor profile',
    estimated_investment_range: 'Intended amount',
  },
};

const GENERIC_FALLBACK = {
  fr: 'Les informations saisies sont incomplètes ou invalides. Merci de vérifier le formulaire.',
  en: 'The information provided is incomplete or invalid. Please review the form.',
};

function fieldLabel(item, lang) {
  const loc = Array.isArray(item?.loc) ? item.loc : [];
  const raw = [...loc].reverse().find((p) => typeof p === 'string' && p !== 'body' && p !== 'query');
  if (!raw) return '';
  return (FIELD_LABELS[lang] || FIELD_LABELS.fr)[raw] || '';
}

/** Traduit UN item de tableau Pydantic ({type, loc, msg, …}) en message maison. */
export function translateValidationItem(item, lang = 'fr') {
  const en = lang === 'en';
  const msg = typeof item?.msg === 'string' ? item.msg : '';
  const type = typeof item?.type === 'string' ? item.type : '';
  const label = fieldLabel(item, lang);

  // 1. Adresse e-mail invalide (EmailStr — y compris domaines réservés .test/.example)
  if (/valid email|email address/i.test(msg) || /email/i.test(type)) {
    return en
      ? 'The e-mail address entered does not appear to be valid. Please check it.'
      : 'L\u2019adresse e-mail saisie ne semble pas valide. Merci de la v\u00e9rifier.';
  }
  // 2. Champ obligatoire manquant
  if (type === 'missing' || /field required/i.test(msg)) {
    if (label) {
      return en
        ? `The \u201c${label}\u201d field is required.`
        : `Le champ \u00ab ${label} \u00bb est obligatoire.`;
    }
    return en ? 'A required field is missing.' : 'Un champ obligatoire n\u2019a pas \u00e9t\u00e9 renseign\u00e9.';
  }
  // 3. Longueur minimale
  const tooShort = /at least (\d+) character/i.exec(msg);
  if (type === 'string_too_short' || tooShort) {
    const n = tooShort ? tooShort[1] : (item?.ctx?.min_length ?? '');
    const suffix = n ? (en ? ` (minimum ${n} characters)` : ` (minimum ${n} caract\u00e8res)`) : '';
    return en
      ? `${label || 'One of the fields'}: the value entered is too short${suffix}.`
      : `${label || 'Un des champs'} : la valeur saisie est trop courte${suffix}.`;
  }
  // 4. Longueur maximale
  const tooLong = /at most (\d+) character/i.exec(msg);
  if (type === 'string_too_long' || tooLong) {
    const n = tooLong ? tooLong[1] : (item?.ctx?.max_length ?? '');
    const suffix = n ? (en ? ` (maximum ${n} characters)` : ` (maximum ${n} caract\u00e8res)`) : '';
    return en
      ? `${label || 'One of the fields'}: the value entered is too long${suffix}.`
      : `${label || 'Un des champs'} : la valeur saisie est trop longue${suffix}.`;
  }
  // 5. Format invalide générique (int/date/URL/enum…)
  if (/is not a valid|input should be|value error|invalid/i.test(msg) || /_type$|_parsing$|enum/i.test(type)) {
    if (label) {
      return en
        ? `The \u201c${label}\u201d field is not in the expected format.`
        : `Le champ \u00ab ${label} \u00bb n\u2019est pas au format attendu.`;
    }
    return en
      ? 'One of the values entered is not in the expected format.'
      : 'Une des valeurs saisies n\u2019est pas au format attendu.';
  }
  // 6. Fallback : JAMAIS le message anglais brut.
  return GENERIC_FALLBACK[en ? 'en' : 'fr'];
}

/** Traduit un `detail` FastAPI complet (tableau Pydantic, objet ou chaîne). */
export function translateValidationDetail(detail, lang = 'fr') {
  if (typeof detail === 'string') return detail; // nos endpoints renvoient déjà du FR métier
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => translateValidationItem(item, lang))
      .filter(Boolean);
    return [...new Set(parts)].join(' ') || GENERIC_FALLBACK[lang === 'en' ? 'en' : 'fr'];
  }
  if (detail && typeof detail === 'object') {
    if (typeof detail.message === 'string') return detail.message; // {error_code, message} maison
    if (typeof detail.msg === 'string') return translateValidationItem(detail, lang);
  }
  return GENERIC_FALLBACK[lang === 'en' ? 'en' : 'fr'];
}

/* Validation CLIENT du format e-mail avant soumission (moins de 422 inutiles).
 * Reproduit les rejets EmailStr les plus courants : format + domaines
 * réservés RFC 2606 (.test, .invalid, .localhost, .example). */
const RESERVED_TLDS = new Set(['test', 'invalid', 'localhost', 'example']);

export function isPlausibleEmail(value) {
  const v = (value || '').trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)) return false;
  const tld = v.split('.').pop().toLowerCase();
  return !RESERVED_TLDS.has(tld);
}

export const EMAIL_CLIENT_ERROR = {
  fr: 'L\u2019adresse e-mail saisie ne semble pas valide. Merci de la v\u00e9rifier.',
  en: 'The e-mail address entered does not appear to be valid. Please check it.',
};
